import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import moment from "moment";

import { colors, icons } from "../themes/base";

import Button from "../common/Button";
import SubProperty from "../common/SubProperty";
import ToolbarInput from "../common/ToolbarInput";
import ToolbarDropdown from "../common/ToolbarDropdown";
import ToolbarTimeInput from "../common/ToolbarTimeInput";
import ToolbarPropDivider from "../common/ToolbarPropDivider";

const StyledWrapper = styled.div`
  padding: ${(props) => props.padding || "0 10px"};
  margin: 1rem 0;
  border-left: ${(props) => props.borderLeft};

  .light-text {
    opacity: 0.5;
    color: ${colors.secondary};
    font-family: Muli;
    font-size: 12px;
    letter-spacing: 0;
    line-height: 15px;
    text-transform: capitalize;
  }

  .text-container {
    height: 37px;
    display: flex;
    font-size: 13px;
    color: ${colors.secondary};
    font-family: Muli;
    letter-spacing: 0;
    line-height: 15px;
    text-transform: capitalize;
    justify-content: space-between;
  }

  .notes-area {
    height: 70px;
    width: 100%;
    margin: 0;
    color: ${colors.secondary};
    font-family: Muli;
    font-size: 13px;
    letter-spacing: 0;
    line-height: 16px;
    border: 0;
    resize: none;
    border-radius: 3px;
    background: rgba(20, 41, 69, 0.03);
    padding: 1rem;

    &:focus {
      outline: 0;
    }

    ::placeholder {
      /* Chrome, Firefox, Opera, Safari 10.1+ */
      color: ${colors.s_light};
      opacity: 1; /* Firefox */
    }

    :-ms-input-placeholder {
      /* Internet Explorer 10-11 */
      color: ${colors.s_light};
    }

    ::-ms-input-placeholder {
      /* Microsoft Edge */
      color: ${colors.s_light};
    }
  }

  .header-container {
    justify-content: space-between;
    display: flex;
    align-items: center;
    width: 100%;

    input {
      background: ${colors.white};
      border: 1px solid transparent;
      color: ${colors.s_light};
      font-family: Muli;
      font-size: 12px;
      font-weight: 400;
      letter-spacing: 0;
      line-height: 15px;

      :focus {
        outline: 0;
        border: 1px solid ${colors.s_extralight};
      }
    }
  }
  .error-msg {
    color: red;
    margin: 1rem;
    font-weight: 600;
  }
`;

const RegionsContainer = styled.div`
  max-height: 67%;
  overflow-y: scroll;
`;

const toSec = (timeFormat) => {
  return moment.duration(timeFormat).asSeconds();
};

const getBorderColor = (start, end, duration) => {
  const s = toSec(start);
  const e = toSec(end);
  const d = toSec(duration);

  if (s > d || e > d || s > e) {
    return "6px solid red";
  }

  return `6px solid ${colors.text_tools.border}`;
};

const RenderErrorMessage = ({ start, end, duration }) => {
  const s = toSec(start);
  const e = toSec(end);
  const d = toSec(duration);

  if (s > d) {
    return <p className="error-msg">Start time greater than video duration</p>;
  }

  if (e > d) {
    return <p className="error-msg">End time greater than video duration</p>;
  }

  if (s > e) {
    return <p className="error-msg">Start time greater than end time</p>;
  }

  return <p></p>;
};

function AudioProperty(props) {
  const [showAddModel, setShowAdd] = useState(false);
  const [start, setStart] = useState("00:00:00.0");
  const [end, setEnd] = useState(props.duration);

  useEffect(() => {
    setEnd(props.duration);
  }, [props.duration, props.resolution]);

  const handleDataChange = (id, property, value) => {
    if (!id) {
      return;
    }

    props.handleChange(id, { [property]: value });
  };

  const initiateState = () => {
    setShowAdd(false);
    setStart("00:00:00.0");
    setEnd(props.duration);
  };

  const handleTick = () => {
    props.handleAdd(start, end);
    initiateState();
  };

  return (
    <React.Fragment>
      <StyledWrapper>
        <SubProperty>
          <p className="light-text">Name</p>
          <ToolbarInput disabled value={props.audioName} />
        </SubProperty>
        <ToolbarPropDivider />
        <SubProperty>
          <p className="light-text">Duration</p>
          <ToolbarInput
            disabled
            value={props.duration}
            onChange={props.setDuration}
          />
        </SubProperty>
        <ToolbarPropDivider />
        <SubProperty>
          <ToolbarDropdown
            width="100%"
            fontSize="13px"
            fontWeight={600}
            value={props.cropType}
            options={[
              { label: "Merge", value: "merge" },
              { label: "Delete", value: "delete" },
              { label: "Split", value: "split" },
              { label: "Redact", value: "redact" },
            ]}
            handleChange={props.setCropType}
          />
        </SubProperty>
        <ToolbarPropDivider />
      </StyledWrapper>

      <RegionsContainer fullScreenMode={props.fullScreenMode}>
        {props.regions.map((rgn, i) => (
          <StyledWrapper
            key={rgn.id}
            padding="0"
            borderLeft={getBorderColor(rgn.start, rgn.end, props.duration)}
          >
            <SubProperty>
              <div className="header-container">
                <input disabled value={`Region ${i + 1}`} />
                <div>
                  <Button
                    padding="7px"
                    background={`url(${icons.IC_Delete}) center center / 16px 16px no-repeat`}
                    onClick={() => props.handleDelete(rgn.id)}
                  />
                </div>
              </div>
            </SubProperty>
            <SubProperty>
              <ToolbarTimeInput
                duration={props.duration}
                label="Start"
                value={rgn.start}
                onChange={(value) => handleDataChange(rgn.id, "start", value)}
              />
            </SubProperty>
            <SubProperty>
              <ToolbarTimeInput
                duration={props.duration}
                label="End"
                value={rgn.end}
                onChange={(value) => handleDataChange(rgn.id, "end", value)}
              />
            </SubProperty>
            <SubProperty>
              <textarea
                className="notes-area"
                placeholder="Notes"
                value={rgn.notes}
                onChange={(e) =>
                  handleDataChange(rgn.id, "notes", e.target.value)
                }
              />
            </SubProperty>
            <RenderErrorMessage
              start={rgn.start}
              end={rgn.end}
              duration={props.duration}
            />
          </StyledWrapper>
        ))}

        {showAddModel && (
          <StyledWrapper
            borderLeft={getBorderColor(start, end, props.duration)}
          >
            <SubProperty>
              <div className="header-container">
                <input disabled value={`Region ${props.regions.length + 1}`} />
                <div>
                  <Button
                    padding="7px"
                    margin="0 10px"
                    background={`url(${icons.IC_Cancel}) center center / 20px 20px no-repeat`}
                    onClick={initiateState}
                  />
                  <Button
                    padding="7px"
                    background={`url(${icons.IC_Tick}) center center / 20px 20px no-repeat`}
                    onClick={handleTick}
                  />
                </div>
              </div>
            </SubProperty>
            <SubProperty>
              <ToolbarTimeInput
                duration={props.duration}
                label="Start"
                value={start}
                onChange={setStart}
              />
            </SubProperty>
            <SubProperty>
              <ToolbarTimeInput
                duration={props.duration}
                label="End"
                value={end}
                onChange={setEnd}
              />
            </SubProperty>
            <RenderErrorMessage
              start={start}
              end={end}
              duration={props.duration}
            />
          </StyledWrapper>
        )}

        <StyledWrapper>
          <SubProperty>
            <Button
              fontSize="10px"
              fontWeight="600"
              color="#142945"
              padding="14px 5px 10px 30px"
              background={`url(${icons.IC_Add_Layer}) left center / 23px 23px no-repeat`}
              onClick={() => setShowAdd(true)}
            >
              Add New Region
            </Button>
          </SubProperty>
        </StyledWrapper>
      </RegionsContainer>
    </React.Fragment>
  );
}

export default AudioProperty;

AudioProperty.propTypes = {
  name: PropTypes.string,
  regions: PropTypes.array,
  duration: PropTypes.string,
  cropType: PropTypes.string,
  setCropType: PropTypes.func,
  handleChange: PropTypes.func,
};

AudioProperty.defaultProps = {
  name: "",
  regions: [],
  duration: "04:33:0",
  cropType: "merge",
  setCropType: () => {},
  handleChange: () => {},
};
